import React, { createContext, useContext, useState, useEffect } from 'react';
import { saveUser, getUser, removeUser, saveToken, getToken } from '@/utils/localStorage';
import { getBaseUrl } from '@/utils/api';

const API_URL = getBaseUrl();
const UserContext = createContext(null);

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifySession = async () => {
      // First check localStorage for cached user
      const savedUser = getUser();
      const savedToken = getToken();
      
      if (savedUser) {
        // Verify with server that session is still valid
        try {
          const headers = {};
          if (savedToken) {
            headers['Authorization'] = `Bearer ${savedToken}`;
          }
          
          const response = await fetch(`${API_URL}/api/auth/me`, {
            credentials: 'include',
            headers
          });
          
          if (response.ok) {
            const serverUser = await response.json();
            // Update with fresh data from server
            setUser(serverUser);
            saveUser(serverUser);
          } else {
            // Session invalid, clear local storage
            console.log('Session expired, clearing local data');
            removeUser();
            setUser(null);
          }
        } catch (error) {
          // Network error - use cached user but don't trust it for long
          console.log('Could not verify session, using cached user');
          setUser(savedUser);
        }
      }
      
      setLoading(false);
    };
    
    verifySession();
  }, []);

  const login = (userData, token = null) => {
    setUser(userData);
    saveUser(userData);
    if (token) {
      saveToken(token);
    }
  };

  const logout = () => {
    setUser(null);
    removeUser();
    // Clear ALL assessment and progress data to prevent cross-user data leakage
    localStorage.removeItem('gntl_assessment');
    localStorage.removeItem('gntl_progress');
    // Clear old-style keys (for backward compatibility)
    localStorage.removeItem('speakingAssessment');
    localStorage.removeItem('speakingAssessmentId');
    localStorage.removeItem('listeningAssessment');
    localStorage.removeItem('listeningAssessmentId');
    localStorage.removeItem('lastSpeakingPractice');
    localStorage.removeItem('lastListeningPractice');
    localStorage.removeItem('voiceReadingProgress'); // Speaking practice progress
    // Clear all user-specific keys by pattern
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('speakingAssessment_') ||
        key.startsWith('listeningAssessment_') ||
        key.startsWith('lastSpeakingPractice_') ||
        key.startsWith('lastListeningPractice_') ||
        key.startsWith('voiceReadingProgress_')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Clear IndexedDB voice commitments to prevent cross-user data
    try {
      const deleteRequest = indexedDB.deleteDatabase('VoiceCommitments');
      deleteRequest.onerror = () => console.log('Error clearing IndexedDB');
      deleteRequest.onsuccess = () => console.log('IndexedDB cleared on logout');
    } catch (e) {
      console.log('IndexedDB clear skipped:', e);
    }
  };

  const updateUser = (updates) => {
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    saveUser(updatedUser);
  };

  return (
    <UserContext.Provider value={{ user, login, logout, updateUser, loading }}>
      {children}
    </UserContext.Provider>
  );
};
